import { PLATFORM_ROLE_DEFINITIONS } from "./platform-roles";
import { splitPermission, type Permission } from "./statement";
import { isUnrestricted, platformRoleOf, type PolicySubject } from "./subject";

export interface CanOptions {
  /** The facility the action targets, adding that facility role's permissions and roles. Absent
   * means "anywhere they work": the organization role or a role at any of their buildings, never a
   * building admin's set (that building's alone). */
  facilityId?: string | null;
}

/** Effective check. Platform admin outside an organization: true. Other platform members: platform
 * set outside one; inside, platform set for platform permissions, else the organization's. Everyone
 * else: organization plus facility(F), union only. */
export function can(
  subject: PolicySubject,
  permission: Permission,
  options: CanOptions = {},
): boolean {
  return holds(subject, permission, options.facilityId ?? null, runsTheCompany(permission));
}

/** Held across the whole organization: its role, or a role that runs the company (the
 * Administrator), never a role held at one building (HR at one is not org HR). */
export function canOrganizationWide(subject: PolicySubject, permission: Permission): boolean {
  return holds(subject, permission, null, true);
}

function holds(
  subject: PolicySubject,
  permission: Permission,
  facilityId: string | null,
  companyOnly: boolean,
): boolean {
  if (isUnrestricted(subject)) return true;
  const platformRole = platformRoleOf(subject);
  if (platformRole) {
    const held = PLATFORM_ROLE_DEFINITIONS[platformRole].platform.includes(permission);
    if (subject.organization === null) return held;
    // The platform console stays reachable while a member is inside an
    // organization; that is how they leave it again.
    if (permission.startsWith("platform:")) return held;
  }
  const organization = subject.organization;
  if (!organization) return false;
  if (organization.status === "suspended") return false;
  // Another operator's building: nothing held in this organization reaches it.
  if (facilityId && !organization.facilityIds.includes(facilityId)) return false;
  if (organization.permissions.includes(permission)) return true;
  if (!facilityId) {
    return Object.values(subject.facilities).some((membership) =>
      (companyOnly ? (membership.companyGrants ?? []) : membership.grants).includes(permission),
    );
  }
  const membership = subject.facilities[facilityId];
  return membership?.permissions.includes(permission) ?? false;
}

/** Held at each of the organization's buildings, for a record covering all of them (the
 * organization-wide payment account). An organization role answers at every building; with no
 * buildings yet only an organization-wide holder does, never a vacuous "every". */
export function canAtEveryBuilding(subject: PolicySubject, permission: Permission): boolean {
  const buildings = subject.organization?.facilityIds ?? [];
  if (buildings.length === 0) return canOrganizationWide(subject, permission);
  return buildings.every((facilityId) => can(subject, permission, { facilityId }));
}

/** Changing company settings (API keys, phone accounts). With no building named, a building role's
 * settings do not answer unless the role runs the company (the Administrator); at its building they
 * do. */
export function runsTheCompany(permission: Permission): boolean {
  const { resource, action } = splitPermission(permission);
  return resource === "settings" && action !== "read";
}

/** Signatures must be attributable to one person; an impersonated session may not sign. */
export function canSign(
  subject: PolicySubject,
  permission: Permission,
  options?: CanOptions,
): boolean {
  if (subject.impersonatedBy) return false;
  return can(subject, permission, options);
}

/** What screens show for the building being viewed: its roles and admin set, or, viewing all,
 * anything they may do at any. The API decides per request. */
export function canAt(
  subject: PolicySubject,
  permission: Permission,
  facilityId: string | null,
): boolean {
  if (facilityId) return can(subject, permission, { facilityId });
  return (
    can(subject, permission) ||
    Object.keys(subject.facilities).some((id) => can(subject, permission, { facilityId: id }))
  );
}
